import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Overlay from '@/forms/_common/Overlay';
import FormErrors from '@/forms/_common/FormErrors';
import RequestError from '@/forms/_common/RequestError';
import ErrorMessage from '@/forms/_common/ErrorMessage';
import FormTemplate from '@/dialogs_common/SetupTemplate';
import FormField from '@/forms/_common/FormField';
import StringField from '@/forms/_fields_v2/StringField';
import PrimaryButton from '@/components_setup/PrimarySetupButton';
import SelectField from '@/forms/_fields_v2/SelectField';
import CheckboxField from '@/forms/_fields_v2/CheckboxField';
import { useQueryMutation } from '@lore/query-connect';
import { useTranslation } from 'react-i18next';

Form.propTypes = {
  user: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func,
  navigation: PropTypes.object,
};

export default function Form(props) {
  const { t } = useTranslation();
  const genderOptions = t('user.demographics.gender.selections', { returnObjects: true });
  const ethnicityOptions = t('user.demographics.ethnicity.selections', { returnObjects: true });

  const genderOptionsFormatted = useMemo(() => ({
    data: genderOptions.map((option) => ({ id: option.value, data: { name: option.label } }))
  }), [genderOptions]);

  const ethnicityOptionsFormatted = useMemo(() => ({
    data: ethnicityOptions.map((option) => ({ id: option.value, data: { name: option.label } }))
  }), [ethnicityOptions]);

  const {
    user,
    navigation,
    onNext,
    onPrevious,
  } = props;

  const [data, setData] = useState(_.pick(user.data, [
    'gender',
    'ethnicity',
    'yearOfBirth',
    'showDemographics'
  ]));
  const [showErrors, setShowErrors] = useState(false);

  const mutation = useQueryMutation('currentUser.updateSingleton', user);
  const { saving, success, error, request } = mutation;

  useEffect(() => {
    if (success) {
      onNext(data);
    }
  }, [success]);

  function onSubmit() {
    mutation.mutate(data);
  }

  function onChange(name, value) {
    setData(_.merge({}, data, {
      [name]: value
    }));
  }

  return (
    <div className="my-auto">
      <Overlay isVisible={saving}>
        <FormErrors data={data} validators={{}}>
          {({ errors, hasError }) => {
            return (
              <FormTemplate
                onPrevious={onPrevious}
                navigation={navigation}
                title="Please tell us more about you."
                subtitle={(
                  <>
                    If you&apos;re comfortable, please share a few details about yourself. This information
                    helps us better understand and support the educators and partners we work with as
                    we continue to grow. What you share will stay private and will only be shared with
                    your permission.
                  </>
                )}
                alert={error ? (
                  <RequestError request={request} v2={true} />
                ) : showErrors ? (
                  <ErrorMessage v2={true}>
                    Please fill in all required fields.
                  </ErrorMessage>
                ) : null}
                body={(
                  <div className="flex flex-col">
                    <div className="w-full sm:w-80 space-y-6 mb-12">
                      <FormField name="gender" data={data} errors={showErrors && errors} onChange={onChange}>
                        {(field) => (
                          <SelectField
                            label="Gender identity"
                            value={field.value}
                            error={field.error}
                            placeholder="Select a value..."
                            options={genderOptionsFormatted}
                            optionLabel="name"
                            onChange={field.onChange}
                          />
                        )}
                      </FormField>
                      <FormField name="ethnicity" data={data} errors={showErrors && errors} onChange={onChange}>
                        {(field) => (
                          <SelectField
                            label="Race and ethnicity"
                            value={field.value}
                            error={field.error}
                            placeholder="Select a value..."
                            options={ethnicityOptionsFormatted}
                            optionLabel="name"
                            onChange={field.onChange}
                          />
                        )}
                      </FormField>
                      <FormField name="yearOfBirth" data={data} errors={showErrors && errors} onChange={onChange}>
                        {(field) => (
                          <StringField
                            label="Year of birth"
                            value={field.value}
                            error={field.error}
                            onChange={field.onChange}
                          />
                        )}
                      </FormField>
                      <FormField name="showDemographics" data={data} errors={showErrors && errors} onChange={onChange}>
                        {(field) => (
                          <CheckboxField
                            label="I give permission to share my demographic information in my profile."
                            checked={field.value}
                            error={field.error}
                            onChange={(event) => field.onChange(event.target.checked)}
                          />
                        )}
                      </FormField>
                    </div>
                  </div>
                )}
                footer={(
                  <>
                    <PrimaryButton disabled={hasError} onClick={() => hasError ? setShowErrors(true) : onSubmit()}>
                      Next
                    </PrimaryButton>
                  </>
                )}
              />
            );
          }}
        </FormErrors>
      </Overlay>
    </div>
  );
};
